/* 17_제품_Shipping §3 — delivery_comparison_table (PBP 배송 비교 테이블).
 * CompareTableCard와 **다른** schema — rule×service 2-depth:
 *   payload: { layout: "table"|"single"|"empty", columns?, rows?, disclaimer?, tables? }
 *   columns: [{ key, label, sub_label?, depth: 1|2 }]  (1열 배송유형 · 2열 Price 고정)
 *   rows:    [{ rule_id, rule_name, price, earliest_delivery, "install:CODE"?, ... }]
 * 렌더: ai-lists.html IS-05 — 배송 유형=열 · PRICE/Installation/…=행, tbody 2단 rowspan.
 * Multi-SKU(§3-2)는 tables[] 배열 — SKU별 섹션 렌더.
 * Disclaimer는 카드 내부에서 제거되어 위젯 하단 챗봇 메시지(summary)에 결합 렌더.
 *
 * 스타일 SSOT: `src/index.css` → `preview/widget.css`(`.ai-gp1--*` · `.gp1-table` · `.pd-cell`)
 * + `preview/widget.legacy.css`(`.ac-shell`만). legacy 단독으로는 테이블 시안이 적용되지 않는다. */

import { useEffect, useRef } from "react";
import { useLabel, useLabels } from "../lib/labels";
import { postToHost } from "../lib/postmessage";
import { useDragScrollbar } from "../lib/useDragScrollbar";
import {
  EMPTY_CELL,
  ShippingData,
  ShippingTable,
  TableColumn,
  TableRow,
  buildRowSpecs,
  cellValue,
  extractServiceFee,
  resolveTables,
} from "./deliveryComparisonTable";

export type { ShippingData, ShippingTable, TableColumn, TableRow };
export { buildRowSpecs, cellValue, extractServiceFee, resolveTables };

interface Props {
  data: ShippingData;
  sessionToken: string;
}

function resolveColumnLabel(
  col: TableColumn,
  labels: Record<string, string | undefined>,
): string {
  const i18nKey = col.label_i18n;
  if (i18nKey && labels[i18nKey]) {
    return labels[i18nKey];
  }
  return col.label;
}

function SingleView({
  table,
  labels,
}: {
  table: ShippingTable;
  labels: Record<string, string | undefined>;
}) {
  const row = (table.rows || [])[0];
  const deliveryLabel = labels.deliveryRuleColumn || "Delivery";
  const priceLabel = labels.priceColumn || "Price";
  const earliestLabel = labels.earliestDeliveryColumn || "Earliest Delivery Date";
  const shipmentLabel = labels.singleShipmentStart || labels.shipmentStartColumn || "Shipment start";

  if (!row) return null;
  const showShipment = Boolean(table.column_visibility?.shipment_start);
  const shipment = showShipment
    ? (row.shipment_start || table.shipment_start_date || "")
    : "";

  return (
    <div className="ai-gp1-detail-container">
      <div className="ai-gp1-scroll--container">
        <div className="ai-gp1-scrollcontent-wrap">
          <div className="ai-gp1-table--list">
            <table className="gp1-table">
              <thead>
                <tr>
                  <th scope="col">
                    <span>{deliveryLabel}</span>
                  </th>
                  <th scope="col">
                    <span>{priceLabel}</span>
                  </th>
                  {row.earliest_delivery && row.earliest_delivery !== EMPTY_CELL ? (
                    <th scope="col">
                      <span>{earliestLabel}</span>
                    </th>
                  ) : null}
                  {shipment && shipment !== EMPTY_CELL ? (
                    <th scope="col">
                      <span>{shipmentLabel}</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td scope="row" className="has-bg-gray">
                    <strong>{row.rule_name}</strong>
                  </td>
                  <td className="aln-right">
                    <div className="pd-cell">{row.price}</div>
                  </td>
                  {row.earliest_delivery && row.earliest_delivery !== EMPTY_CELL ? (
                    <td>
                      <div className="pd-cell">{row.earliest_delivery}</div>
                    </td>
                  ) : null}
                  {shipment && shipment !== EMPTY_CELL ? (
                    <td>
                      <div className="pd-cell">{shipment}</div>
                    </td>
                  ) : null}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function TableView({
  table,
  columnLabel,
}: {
  table: ShippingTable;
  columnLabel: (col: TableColumn) => string;
  labels?: Record<string, string | undefined>;
}) {
  const columns = table.columns || [];
  const ruleRows = table.rows || [];
  const rowSpecs = buildRowSpecs(columns);

  const labelForKey = (columnKey: string) => {
    const col = columns.find((c) => c.key === columnKey);
    return col ? columnLabel(col) : columnKey;
  };

  const scrollRef = useRef<HTMLDivElement>(null!);
  const barRef = useRef<HTMLDivElement>(null!);
  const trackRef = useRef<HTMLDivElement>(null!);
  const scrollbarRef = useRef<HTMLDivElement>(null!);
  useDragScrollbar(scrollRef, barRef, trackRef, scrollbarRef, [
    ruleRows.length,
    rowSpecs.length,
  ]);

  if (!ruleRows.length || !rowSpecs.length) return null;

  return (
    <div className="ai-gp1-detail-container">
      <div
        ref={scrollRef}
        className="ai-gp1-scroll--container"
        style={{ overscrollBehaviorX: "contain", touchAction: "pan-x" }}
      >
        <div className="ai-gp1-scrollcontent-wrap">
          <div className="ai-gp1-table--list">
            <table className="gp1-table">
              <thead>
                <tr>
                  <th scope="col" colSpan={2} style={{}}>
                    <span />
                  </th>
                  {ruleRows.map((rule, idx) => (
                    <th
                      key={rule.rule_id || `rule-${idx}`}
                      scope="col"
                      className={idx === 0 ? "col" : undefined}
                      style={{}}
                    >
                      <span>{rule.rule_name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowSpecs.map((spec) => {
                  if (spec.kind === "group") {
                    return spec.subs.map((sub, subIdx) => (
                      <tr key={`${spec.label}-${sub.columnKey}`}>
                        {subIdx === 0 ? (
                          <td
                            scope="row"
                            rowSpan={spec.subs.length}
                            className="has-bg-gray"
                            style={{}}
                          >
                            <div className="pd-cell">
                              <strong>{spec.label}</strong>
                            </div>
                          </td>
                        ) : null}
                        <td scope="row" className="has-bg-gray" style={{}}>
                          <div className="pd-cell">
                            <strong>{sub.label}</strong>
                          </div>
                        </td>
                        {ruleRows.map((rule, ruleIdx) => (
                          <td key={rule.rule_id || `rule-${ruleIdx}`}>
                            <div className="pd-cell">
                              {extractServiceFee(
                                String(rule[sub.columnKey] ?? EMPTY_CELL),
                              )}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ));
                  }

                  return (
                    <tr key={spec.columnKey}>
                      <td
                        scope="row"
                        colSpan={2}
                        className="has-bg-gray"
                        style={{}}
                      >
                        <div className="pd-cell">
                          <strong>{labelForKey(spec.columnKey)}</strong>
                        </div>
                      </td>
                      {ruleRows.map((rule, ruleIdx) => {
                        const value = cellValue(rule, spec);
                        if (Array.isArray(value)) {
                          return (
                            <td key={rule.rule_id || `rule-${ruleIdx}`}>
                              {value.map((part) => (
                                <div
                                  key={`${rule.rule_id}-${part}`}
                                  className="pd-cell"
                                >
                                  {part}
                                </div>
                              ))}
                            </td>
                          );
                        }
                        return (
                          <td key={rule.rule_id || `rule-${ruleIdx}`}>
                            <div className="pd-cell">{value}</div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div ref={scrollbarRef} className="ai-gp1-custom--scrollbar hidden">
        <div ref={trackRef} className="ai-gp1-scrollbar--track">
          <div ref={barRef} className="ai-gp1-scrollbar--bar" />
        </div>
      </div>
    </div>
  );
}

export default function DeliveryComparisonTableCard({ data, sessionToken }: Props) {
  const tables = resolveTables(data);
  const labels = useLabels();
  const noDataLabel = useLabel("noData", "Delivery information is unavailable.");
  const columnLabel = (col: TableColumn) => resolveColumnLabel(col, labels);

  const hasVisibleTable =
    tables.some((t) => t.table.layout === "table") ||
    (tables.length > 1 && tables.some((t) => t.table.layout !== "empty"));

  useEffect(() => {
    if (!hasVisibleTable) {
      postToHost("widget.resize", { width: 0, height: 0 }, { sessionId: sessionToken });
      return;
    }
    const report = () => {
      const root = document.querySelector(
        ".ac-shell, .ac-shell--plain, .ac-shell--white",
      ) as HTMLElement | null;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const topGap = Math.max(0, Math.round(rect.top + window.scrollY));
      const height = Math.ceil(root.scrollHeight) + topGap;
      const width = Math.ceil(rect.width);
      postToHost("widget.resize", { width, height }, { sessionId: sessionToken });
    };
    report();
    const t = window.setTimeout(report, 120);
    return () => window.clearTimeout(t);
  }, [tables, sessionToken, hasVisibleTable]);

  if (!tables.length) {
    return (
      <div className="ai-gp1--section">
        <p style={{ textAlign: "center", color: "#646464" }}>{noDataLabel}</p>
      </div>
    );
  }

  if (!hasVisibleTable) {
    return null;
  }

  return (
    <div className="ai-gp1--section ac-shipping">
      {tables.map(({ sku, table }, idx) => {
        const modelName = sku ? sku.split(".")[0] : "";
        return (
          <div
            key={sku || `table-${idx}`}
            className="ac-shipping__section"
            style={{ marginBottom: idx < tables.length - 1 ? "24px" : "0" }}
          >
            {tables.length > 1 && (modelName || sku) ? (
              <p className="ac-shipping__sku">
                {modelName || sku}
              </p>
            ) : null}
            {table.layout === "single" ? (
              <SingleView table={table} labels={labels} />
            ) : table.layout === "empty" ? null : (
              <TableView table={table} columnLabel={columnLabel} labels={labels} />
            )}
          </div>
        );
      })}
    </div>
  );
}
